/**
 * Service for managing Bluetooth button (camera shutter remote) detection settings
 *
 * These devices (like "AB Shutter 3") work as Bluetooth HID keyboards
 * and send the ENTER key (keycode 13) when the "Android" button is pressed.
 */

const STORAGE_KEY = 'bluetooth_button_enabled';
const COOLDOWN_MS = 2000; // 2 second cooldown between triggers

// Global timestamp of last trigger (not persisted, resets on page reload)
let lastGlobalTriggerTime = 0;

export const bluetoothButtonService = {
  /**
   * Check if Bluetooth button detection is enabled in localStorage
   * Default is ENABLED (true) if no stored value exists
   * @returns {boolean}
   */
  isEnabled: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Default to enabled if no value stored
      if (stored === null) {
        return true;
      }
      return stored === 'true';
    } catch (error) {
      console.error('Error reading Bluetooth button setting:', error);
      return true; // Default to enabled on error
    }
  },

  /**
   * Enable Bluetooth button detection
   */
  enable: () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.error('Error saving Bluetooth button setting:', error);
    }
  },

  /**
   * Disable Bluetooth button detection
   */
  disable: () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'false');
    } catch (error) {
      console.error('Error saving Bluetooth button setting:', error);
    }
  },

  /**
   * Toggle Bluetooth button detection
   * @returns {boolean} - New state
   */
  toggle: () => {
    const current = bluetoothButtonService.isEnabled();
    if (current) {
      bluetoothButtonService.disable();
    } else {
      bluetoothButtonService.enable();
    }
    return !current;
  },

  /**
   * Check if enough time has passed since last trigger (cooldown check)
   * @returns {boolean} - True if can trigger now
   */
  canTriggerNow: () => {
    const now = Date.now();
    const timeSinceLastTrigger = now - lastGlobalTriggerTime;
    const canTrigger = timeSinceLastTrigger >= COOLDOWN_MS;

    if (!canTrigger) {
      console.log('Bluetooth button cooldown active:', {
        timeSinceLastTrigger,
        cooldownMs: COOLDOWN_MS,
        remainingMs: COOLDOWN_MS - timeSinceLastTrigger
      });
    }

    return canTrigger;
  },

  /**
   * Update the global last trigger timestamp
   * Call this after a Bluetooth button press is successfully triggered
   */
  setLastTriggerTime: () => {
    lastGlobalTriggerTime = Date.now();
    console.log('Global Bluetooth button timestamp updated:', lastGlobalTriggerTime);
  },
};
